import React from "react";
import { Bluelagoon, useBluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import Message from "../components/Message";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@shadcn-in-nx/ui/alert-dialog";

export function WhatsAppEmbeddedModal({ onClose, onSuccess }) {
    const { t } = useTranslation();
    const [step, setStep] = React.useState("checklist");
    const [setupData, setSetupData] = React.useState({});
    const [isLoading, setIsLoading] = React.useState(false);
    const [facebookSDKLoaded, setFacebookSDKLoaded] = React.useState(false);
    const [showConfirmClose, setShowConfirmClose] = React.useState(false);
    const [showVerificationGuide, setShowVerificationGuide] = React.useState(false);
    const [checklist, setChecklist] = React.useState({
        businessVerified: false,
        numberAdded: false,
    });

    const { call: createIntegration } = useBluelagoon({
        method: "createIntegration",
    });

    // Check if setup process has started
    const hasSetupStarted =
        (step !== "checklist" && step !== "intro") ||
        isLoading ||
        Object.keys(setupData).length > 0;

    const handleClose = () => {
        if (hasSetupStarted) {
            setShowConfirmClose(true);
        } else {
            onClose();
        }
    };

    const handleConfirmClose = () => {
        setShowConfirmClose(false);
        onClose();
    };

    const handleCancelClose = () => {
        setShowConfirmClose(false);
    };

    const handleChecklistChange = (item) => {
        setChecklist((prev) => ({
            ...prev,
            [item]: !prev[item],
        }));
    };

    const isChecklistComplete = () => {
        return checklist.businessVerified && checklist.numberAdded;
    };

    const proceedFromChecklist = () => {
        if (isChecklistComplete()) {
            setStep("intro");
        }
    };

    React.useEffect(() => {
        const loadFacebookSDK = () => {
            if (window.FB) {
                setFacebookSDKLoaded(true);
                return;
            }

            window.fbAsyncInit = function () {
                if (window.FB) {
                    try {
                        window.FB.init({
                            appId: Bluelagoon.settings.whatsapp.appId,
                            autoLogAppEvents: true,
                            xfbml: true,
                            version: "v20.0",
                        });
                        setFacebookSDKLoaded(true);
                    } catch (error) {
                        console.error(
                            "Error initializing Facebook SDK:",
                            error
                        );
                        setFacebookSDKLoaded(false);
                        toast.error(
                            t("integrations.whatsappSetup.errors.sdkInitFailed")
                        );
                    }
                }
            };

            if (
                !document.querySelector(
                    'script[src*="connect.facebook.net/en_US/sdk.js"]'
                )
            ) {
                console.log("loading script");
                const script = document.createElement("script");
                script.async = true;
                script.defer = true;
                script.crossOrigin = "anonymous";
                script.src = "https://connect.facebook.net/en_US/sdk.js";
                script.onload = () => {
                    console.log("Facebook SDK loaded successfully");
                };
                script.onerror = () => {
                    setFacebookSDKLoaded(false);
                    toast.error(
                        t("integrations.whatsappSetup.errors.sdkLoadFailed")
                    );
                };
                document.head.appendChild(script);
            }
        };

        const handleMessage = (event) => {
            if (!event.origin.endsWith("facebook.com")) return;

            let data;
            try {
                // Try to parse as JSON first
                if (typeof event.data === "string") {
                    data = JSON.parse(event.data);
                } else {
                    data = event.data;
                }
            } catch (error) {
                // If it's not JSON, it might be a callback string with auth code
                if (
                    typeof event.data === "string" &&
                    event.data.includes("code=")
                ) {
                    // Extract the code from the callback URL
                    const urlParams = new URLSearchParams(
                        event.data.split("?")[1] || event.data
                    );
                    const authCode = urlParams.get("code");
                    if (authCode) {
                        setSetupData((prev) => ({
                            ...prev,
                            authCode: authCode,
                        }));
                        console.log("Captured auth code from callback");
                    }
                }
                return;
            }

            if (data && data.type === "WA_EMBEDDED_SIGNUP") {
                if (
                    data.event === "FINISH" ||
                    data.event === "FINISH_ONLY_WABA" ||
                    data.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"
                ) {
                    setSetupData((prev) => ({
                        ...prev,
                        phoneNumberId: data.data.phone_number_id,
                        wabaId: data.data.waba_id,
                        businessId: data.data.business_id,
                        accessToken: data.data.access_token, // Get access token from embedded signup
                    }));

                    setStep("review");
                } else if (data.event === "CANCEL") {
                    toast.error(t("integrations.whatsappSetup.errors.setupCancelled"));
                    setStep("intro");
                }
            }
        };

        window.addEventListener("message", handleMessage);
        loadFacebookSDK();

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    const fbLoginCallback = (response) => {
        if (response.authResponse) {
            const code = response.authResponse.code;
            setSetupData((prev) => ({
                ...prev,
                authCode: code,
            }));
            toast.success(t("integrations.whatsappSetup.success.connected"));
        } else {
            console.error("WhatsApp login failed:", response);
            toast.error(t("integrations.whatsappSetup.errors.loginFailed"));
        }
    };

    const launchWhatsAppSignup = () => {
        if (!facebookSDKLoaded || !window.FB || !window.FB.login) {
            toast.error(
                t("integrations.whatsappSetup.errors.notReady")
            );
            return;
        }

        if (
            window.location.protocol !== "https:" &&
            window.location.hostname !== "localhost"
        ) {
            toast.error(
                t("integrations.whatsappSetup.errors.requiresHttps")
            );
            return;
        }

        setIsLoading(true);
        try {
            window.FB.login(fbLoginCallback, {
                config_id: Bluelagoon.settings.whatsapp.configId,
                response_type: "code",
                override_default_response_type: true,
                extras: {
                    setup: {},
                    featureType: "",
                    sessionInfoVersion: "3",
                },
            });
        } catch (error) {
            console.error("Error launching WhatsApp signup:", error);
            toast.error(t("integrations.whatsappSetup.errors.launchFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateIntegration = async () => {
        if (
            !setupData.phoneNumberId ||
            !setupData.wabaId ||
            !setupData.businessId ||
            (!setupData.authCode && !setupData.accessToken)
        ) {
            toast.error(
                t("integrations.whatsappSetup.errors.missingData")
            );
            return;
        }

        setIsLoading(true);
        try {
            await createIntegration({
                platform: "whatsapp",
                type: "whatsapp",
                name: "My WhatsApp Business Integration",
                config: {
                    name: "My WhatsApp Business Integration",
                    phoneNumberId: setupData.phoneNumberId,
                    wabaId: setupData.wabaId,
                    businessId: setupData.businessId,
                    accessToken: setupData.authCode || setupData.accessToken,
                },
            });
            toast.success(t("integrations.whatsappSetup.success.created"));
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Error creating WhatsApp integration:", error);
            toast.error(t("integrations.whatsappSetup.errors.createFailed"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open={true} onOpenChange={handleClose}>
                <DialogContent
                    className="w-full max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col"
                    onPointerDownOutside={(e) => {
                        // Prevent closing when clicking outside during setup
                        if (hasSetupStarted) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <img
                                src="/icons/whatsapp.png"
                                alt="WhatsApp"
                                className="w-8 h-8"
                            />
                            {t("integrations.whatsappSetup.title")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("integrations.whatsappSetup.description")}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto space-y-4 py-2 px-1">
                        {step === "checklist" && (
                            <div className="space-y-4">
                                <Message>
                                    <div className="space-y-3">
                                        <h4 className="font-medium text-orange-800">
                                            {t("integrations.whatsappSetup.checklist.title")}
                                        </h4>
                                        <p className="text-sm text-orange-700">
                                            {t("integrations.whatsappSetup.checklist.description")}
                                        </p>
                                    </div>
                                </Message>

                                <div className="space-y-3">
                                    <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checklist.businessVerified}
                                                onChange={() => handleChecklistChange("businessVerified")}
                                                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm">
                                                    {t("integrations.whatsappSetup.checklist.verifyBusiness")}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">
                                                    {t("integrations.whatsappSetup.checklist.verifyBusinessHelp")}
                                                </div>
                                                <div className="mt-2 flex flex-col gap-2">
                                                    <a
                                                        href="https://www.facebook.com/business/help/2058515294227817"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm w-fit"
                                                    >
                                                        <span>{t("integrations.whatsappSetup.checklist.verifyBusinessLink")}</span>
                                                        <Icon name="ExternalLink" className="w-3 h-3" />
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowVerificationGuide(!showVerificationGuide)}
                                                        className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-xs sm:text-sm w-fit"
                                                    >
                                                        <Icon name={showVerificationGuide ? "ChevronUp" : "ChevronDown"} className="w-3 h-3" />
                                                        <span>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.title")}</span>
                                                    </button>
                                                </div>
                                                {showVerificationGuide && (
                                                    <div className="mt-3 bg-gray-50 rounded-lg p-3 space-y-2">
                                                        <ol className="list-decimal list-inside space-y-1.5 text-xs sm:text-sm text-gray-700">
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step1")}</li>
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step2")}</li>
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step3")}</li>
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step4")}</li>
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step5")}</li>
                                                            <li>{t("integrations.whatsappSetup.checklist.verifyBusinessSteps.step6")}</li>
                                                        </ol>
                                                        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2 mt-2">
                                                            {t("integrations.whatsappSetup.checklist.verifyBusinessSteps.tip")}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>

                                    <div className="border rounded-lg p-3 sm:p-4 space-y-3">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checklist.numberAdded}
                                                onChange={() => handleChecklistChange("numberAdded")}
                                                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm">
                                                    {t("integrations.whatsappSetup.checklist.addNumber")}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">
                                                    {t("integrations.whatsappSetup.checklist.addNumberHelp")}
                                                </div>
                                                <div className="mt-2">
                                                    <a
                                                        href="https://business.facebook.com/latest/whatsapp_manager/overview"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm"
                                                    >
                                                        <span>{t("integrations.whatsappSetup.checklist.addNumberLink")}</span>
                                                        <Icon name="ExternalLink" className="w-3 h-3" />
                                                    </a>
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={proceedFromChecklist}
                                        disabled={!isChecklistComplete()}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        <Icon name="ArrowRight" className="w-5 h-5" />
                                        {t("integrations.whatsappSetup.checklist.continueSetup")}
                                    </button>

                                    <button
                                        onClick={onClose}
                                        className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        {t("common.cancel")}
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "intro" && (
                            <div className="space-y-4">
                                <Message>
                                    <div className="space-y-3">
                                        <h4 className="font-medium">
                                            {t("integrations.whatsappSetup.intro.title")}
                                        </h4>
                                        <ol className="list-decimal list-inside space-y-2 text-sm">
                                            <li>{t("integrations.whatsappSetup.intro.step1")}</li>
                                            <li>{t("integrations.whatsappSetup.intro.step2")}</li>
                                            <li>{t("integrations.whatsappSetup.intro.step3")}</li>
                                            <li>{t("integrations.whatsappSetup.intro.step4")}</li>
                                        </ol>
                                    </div>
                                </Message>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={launchWhatsAppSignup}
                                        disabled={!facebookSDKLoaded || isLoading}
                                        className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#22c55e] text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        ) : (
                                            <Icon name="MessageSquare" className="w-5 h-5" />
                                        )}
                                        {facebookSDKLoaded
                                            ? t("integrations.whatsappSetup.intro.connectButton")
                                            : t("integrations.whatsappSetup.intro.loading")}
                                    </button>

                                    <button
                                        onClick={handleClose}
                                        className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        {t("common.cancel")}
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "review" && (
                            <div className="space-y-4">
                                <Message variant="success">
                                    <div className="space-y-2">
                                        <h4 className="font-medium">
                                            {t("integrations.whatsappSetup.review.title")}
                                        </h4>
                                        <p className="text-sm">
                                            {t("integrations.whatsappSetup.review.description")}
                                        </p>
                                    </div>
                                </Message>

                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <h4 className="font-medium text-sm">
                                        {t("integrations.whatsappSetup.review.connectionDetails")}
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <span className="text-gray-600">
                                                {t("integrations.whatsappSetup.review.phoneNumberId")}
                                            </span>
                                            <span className="ml-2 font-mono">
                                                {setupData.phoneNumberId}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">
                                                {t("integrations.whatsappSetup.review.businessAccountId")}
                                            </span>
                                            <span className="ml-2 font-mono">
                                                {setupData.wabaId}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">
                                                {t("integrations.whatsappSetup.review.businessPortfolioId")}
                                            </span>
                                            <span className="ml-2 font-mono">
                                                {setupData.businessId}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={handleCreateIntegration}
                                        disabled={isLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        ) : (
                                            <Icon name="Check" className="w-5 h-5" />
                                        )}
                                        {t("integrations.whatsappSetup.review.completeSetup")}
                                    </button>

                                    <button
                                        onClick={() => setStep("intro")}
                                        className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                        disabled={isLoading}
                                    >
                                        {t("integrations.whatsappSetup.review.back")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={showConfirmClose}
                onOpenChange={setShowConfirmClose}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t("integrations.whatsappSetup.confirmClose.title")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("integrations.whatsappSetup.confirmClose.description")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelClose}>
                            {t("integrations.whatsappSetup.confirmClose.continueSetup")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmClose}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {t("integrations.whatsappSetup.confirmClose.cancelSetup")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
